"use client";

import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import {
  clearClientDevRequests,
  type DevRequestEntry,
  getClientDevRequestsSnapshot,
  subscribeClientDevRequests,
} from "@/lib/dev-requests";

const POLL_INTERVAL_MS = 1500;
/** Delayed re-fetch after a navigation, to catch RSC fetches still in flight. */
const SETTLE_DELAY_MS = 1000;
const EMPTY_ENTRIES: DevRequestEntry[] = [];

const getEmptySnapshot = () => EMPTY_ENTRIES;

const formatUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

const statusClass = (status: number) => {
  if (status >= 200 && status < 400) {
    return "text-emerald-400";
  }
  if (status === 0) {
    return "text-neutral-400";
  }
  return "text-red-400";
};

/**
 * Dev-only bar showing every request made through `lib/methods.ts`:
 * client-side calls (TanStack Query and plain fetches) are pushed into an
 * in-memory store, server-side RSC/route-handler calls are read from
 * the /api/dev-requests ring buffer.
 *
 * The server buffer is fetched on mount, on window focus, on route change
 * (plus a delayed settle re-fetch) and while the panel is expanded —
 * there is no continuous background polling when the bar is collapsed.
 */
export const DevRequestBar = () => {
  const [expanded, setExpanded] = useState(false);
  const [serverEntries, setServerEntries] =
    useState<DevRequestEntry[]>(EMPTY_ENTRIES);
  const pathname = usePathname();

  const clientEntries = useSyncExternalStore(
    subscribeClientDevRequests,
    getClientDevRequestsSnapshot,
    getEmptySnapshot,
  );

  const fetchServerEntries = useCallback(async () => {
    try {
      const response = await fetch("/api/dev-requests");
      if (!response.ok) {
        return;
      }
      const { data } = (await response.json()) as {
        data: DevRequestEntry[];
      };
      setServerEntries(data);
    } catch {
      // dev server restarting — the next trigger will retry
    }
  }, []);

  useEffect(() => {
    fetchServerEntries();
    const settle = setTimeout(fetchServerEntries, SETTLE_DELAY_MS);
    return () => clearTimeout(settle);
  }, [fetchServerEntries, pathname]);

  useEffect(() => {
    const onFocus = () => fetchServerEntries();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchServerEntries]);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    fetchServerEntries();
    const interval = setInterval(() => {
      // keep the open-panel poll from running in a background tab
      if (document.visibilityState !== "hidden") {
        fetchServerEntries();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [expanded, fetchServerEntries]);

  const clearAll = useCallback(() => {
    clearClientDevRequests();
    setServerEntries(EMPTY_ENTRIES);
    fetch("/api/dev-requests", { method: "DELETE" }).catch(() => {
      // dev server restarting — buffer will be empty anyway
    });
  }, []);

  const entries = [...serverEntries, ...clientEntries].sort(
    (a, b) => b.startedAt - a.startedAt,
  );

  return (
    <div className="fixed right-2 bottom-2 z-[9999] flex flex-col items-end font-mono text-xs">
      {expanded && (
        <div className="mb-1 flex max-h-80 w-[38rem] max-w-[calc(100vw-1rem)] flex-col rounded-lg border border-neutral-700 bg-neutral-900/95 text-neutral-200 shadow-xl">
          <div className="flex items-center justify-between border-neutral-700 border-b px-3 py-2">
            <span className="font-semibold">
              Requests via lib/methods.ts (last 300)
            </span>
            <button
              className="rounded border border-neutral-600 px-2 py-0.5 hover:bg-neutral-700"
              onClick={clearAll}
              type="button"
            >
              Clear
            </button>
          </div>
          <ul className="overflow-auto px-3 py-1">
            {entries.length === 0 && (
              <li className="py-2 text-neutral-400">No requests yet</li>
            )}
            {entries.map((entry) => (
              <li
                className="flex items-baseline gap-2 border-neutral-800 border-b py-1 last:border-b-0"
                key={`${entry.source}-${entry.id}`}
              >
                <span
                  className={
                    entry.source === "server"
                      ? "shrink-0 rounded bg-sky-900 px-1 text-sky-300"
                      : "shrink-0 rounded bg-amber-900 px-1 text-amber-300"
                  }
                >
                  {entry.source === "server" ? "SRV" : "CLI"}
                </span>
                <span className="w-12 shrink-0">{entry.method}</span>
                <span className={`w-8 shrink-0 ${statusClass(entry.status)}`}>
                  {entry.status || "ERR"}
                </span>
                <span className="min-w-0 flex-1 truncate" title={entry.url}>
                  {formatUrl(entry.url)}
                </span>
                <span className="w-16 shrink-0 text-right text-neutral-400">
                  {Math.round(entry.durationMs)} ms
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        aria-expanded={expanded}
        className="rounded-full border border-neutral-700 bg-neutral-900/95 px-3 py-1.5 text-neutral-200 shadow-lg hover:bg-neutral-800"
        onClick={() => setExpanded((value) => !value)}
        type="button"
      >
        ⚡ {entries.length} req · SRV {serverEntries.length} · CLI{" "}
        {clientEntries.length}
      </button>
    </div>
  );
};
